import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { Plus, UserForm } from "shared/assets/icons";
import { Button, Card, Loader, Message, SocialSecurityFormTeaser } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingUserDocuments } from "Bands/utils";

const formCopy = copy.userSocialSecurityForm;

export const SocialSecurityForms: FC = () => {
  const {
    socialSecurityForms,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteModal,
    getItemActions,
  } = useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.socialSecurityForm,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.socialSecurityForm,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingSocialSecurityForms } = useFetchingUserDocuments();

  const content = useMemo(() => {
    if (fetchingSocialSecurityForms) {
      return <Loader />;
    }

    if (!socialSecurityForms?.length) {
      return <Message message={formCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {socialSecurityForms?.map(form => (
          <SocialSecurityFormTeaser key={form.id} {...form} actions={getActions(form.id)} />
        ))}
      </GridContainer>
    );
  }, [socialSecurityForms, fetchingSocialSecurityForms, getActions]);

  return (
    <Card icon={UserForm} title={formCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.socialSecurityForm)}
          data-testid="add_social_security_form_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{formCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
